import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAITerms } from '../../hooks/useAITerms';
import AITermsModal from './AITermsModal';
import LoadingIndicator from '../../common/Loading';
import ServiceInterruptionOverlay from './ServiceInterruptionOverlay';
import { useServiceInterruptionRetry } from '../../hooks/useServiceInterruptionRetry';
import { TermsType } from '../../services/aiTermsService';
import userProfileAPI from '../../services/userProfileAPI';
import { getStoredAuthToken, endSessionAndRedirect, LOGIN_REASONS } from '../../utils/sessionAuth';
import { isActivePortalFullScreenHandoff } from '../../utils/portalFullScreenChat';

interface AITermsWrapperProps {
    children: React.ReactNode;
    onTermsAccepted?: () => void;
}

const AITermsWrapper: React.FC<AITermsWrapperProps> = ({
    children,
    onTermsAccepted
}) => {
    const termsType = useMemo<TermsType>(() => {
        try {
            const userData = JSON.parse(localStorage.getItem("user") || "{}");
            const platformContext = userData?.user?.platformContext;
            return platformContext === 'aria_chat' ? 'aria_chat' : 'aptive_index';
        } catch {
            return 'aptive_index';
        }
    }, []);

    const isAriaChatUser = termsType === 'aria_chat';

    const {
        hasAcceptedTerms,
        isLoading,
        acceptTerms,
        currentVersion,
        userVersion,
        needsUpdate,
        isUnauthenticated,
        isServiceInterrupted,
        checkStatus,
    } = useAITerms({ termsType });

    const isAuthenticated = Boolean(getStoredAuthToken());

    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(false);
    const [onboardingComplete, setOnboardingComplete] = useState<boolean | null>(null);
    const [retryGeneration, setRetryGeneration] = useState(0);
    const [hasOldUser] = useState<boolean>(() => {
        const stored = localStorage.getItem('oldUser');
        if (!stored) return false;
        try {
            return Boolean(JSON.parse(stored));
        } catch (error) {
            console.error('Error parsing oldUser:', error);
            return false;
        }
    });

    useEffect(() => {
        const checkOnboardingStatus = async () => {
            if (!isAriaChatUser || !isAuthenticated) {
                setOnboardingComplete(true);
                return;
            }

            try {
                setIsCheckingOnboarding(true);
                const profile = await userProfileAPI.getUserProfile();
                const onboardingStatus = profile.ariaOnboarding?.status;
                const isComplete = onboardingStatus === 'assessment_completed' || onboardingStatus === 'first_chat_complete';
                setOnboardingComplete(isComplete);
            } catch (error) {
                console.error('Error checking onboarding status:', error);
                setOnboardingComplete(true);
            } finally {
                setIsCheckingOnboarding(false);
            }
        };

        checkOnboardingStatus();
    }, [isAriaChatUser, isAuthenticated]);

    useEffect(() => {
        const shouldShow = !hasOldUser &&
                          !isLoading &&
                          !hasAcceptedTerms &&
                          (onboardingComplete !== false) &&
                          !isCheckingOnboarding;

        setShowModal(shouldShow);
    }, [hasOldUser, hasAcceptedTerms, isLoading, onboardingComplete, isCheckingOnboarding]);

    useEffect(() => {
        if (isUnauthenticated) {
            endSessionAndRedirect(LOGIN_REASONS.session_expired);
        }
    }, [isUnauthenticated]);

    const triggerTermsRetry = useCallback(() => {
        setRetryGeneration((n) => n + 1);
        void checkStatus({ force: true });
    }, [checkStatus]);

    const interruption = useServiceInterruptionRetry({
        isActive: isServiceInterrupted,
        retryGeneration,
        onRetry: triggerTermsRetry,
    });

    const handleAcceptTerms = async () => {
        try {
            setIsSubmitting(true);
            await acceptTerms();
            setShowModal(false);
            onTermsAccepted?.();
        } catch (error) {
            console.error('Failed to accept terms:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseModal = () => {
        // Terms must be accepted — modal cannot be dismissed
    };

    if (!isAuthenticated || isUnauthenticated) {
        return <>{children}</>;
    }

    if (isServiceInterrupted) {
        return (
            <ServiceInterruptionOverlay
                secondsRemaining={interruption.secondsRemaining}
                totalSeconds={interruption.stage.retryDelaySeconds}
                title={interruption.stage.title}
                message={interruption.stage.message}
                showProgressBar={interruption.stage.showProgressBar}
                showCountdown={interruption.showCountdown}
                onReturnToLogin={interruption.returnToLogin}
                secondaryAction={{ label: 'Try again', onClick: interruption.retryNow }}
            />
        );
    }

    if (hasOldUser) {
        return <>{children}</>;
    }

    if (isAriaChatUser && (isCheckingOnboarding || onboardingComplete === null)) {
        return <LoadingIndicator />;
    }

    if (isAriaChatUser && onboardingComplete === false) {
        return <>{children}</>;
    }

    if (isLoading && !isActivePortalFullScreenHandoff()) {
        return <LoadingIndicator />;
    }

    if (!hasAcceptedTerms) {
        return (
            <>
                <AITermsModal
                    isOpen={showModal}
                    onAccept={handleAcceptTerms}
                    onClose={handleCloseModal}
                    isSubmitting={isSubmitting}
                    currentVersion={currentVersion}
                    userVersion={userVersion}
                    needsUpdate={needsUpdate}
                    termsType={termsType}
                />
            </>
        );
    }

    return <>{children}</>;
};

export default AITermsWrapper;
